// AM Board — drag projects between PMs / unassigned column.
// Persists via POST /api/pcc/programs/:id/assign-pm.
import { useEffect, useState } from "react";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../../App";
import { fetchJson, fmtMoneyShort, healthBadge, initialsOf } from "./pccHelpers";

export default function PccAmBoard() {
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [pcc, us] = await Promise.all([fetchJson("/planning-command-center"), fetchJson("/users")]);
      setData(pcc);
      setUsers(us);
    } catch (e) { toast.error("Failed to load AM board"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex items-center justify-center py-20 text-white/50"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>;

  const pms = users.filter(u => ["PM", "Production Manager", "Director", "COO", "Admin"].includes(u.role));
  // Build columns: unassigned + each PM
  const columns = [
    { id: null, label: "Unassigned" },
    ...pms.map(pm => ({ id: pm.id, label: pm.name })),
  ];
  const projectsByPM = {};
  for (const col of columns) projectsByPM[col.id || "_un"] = [];
  for (const p of (data?.projects || [])) {
    const key = p.pm_id || "_un";
    if (!projectsByPM[key]) projectsByPM[key] = [];
    projectsByPM[key].push(p);
  }

  async function drop(targetPmId) {
    if (!dragging) return;
    const projectId = dragging.id;
    const prev = dragging.pm_id || null;
    setDragging(null);
    if (prev === targetPmId) return;
    try {
      await apiClient.post(`/pcc/programs/${projectId}/assign-pm`, { pm_id: targetPmId || "" });
      toast.success("Reassigned");
      load();
    } catch { toast.error("Failed to reassign"); }
  }

  return (
    <div className="space-y-4" data-testid="pcc-am-board">
      <div>
        <h3 className="text-lg font-semibold text-white">AM Board</h3>
        <p className="text-white/55 text-sm">Drag project cards between PMs (or to Unassigned).</p>
      </div>
      <div className="grid gap-3 overflow-x-auto" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(240px, 1fr))` }}>
        {columns.map(col => (
          <Card
            key={col.id || "un"}
            className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden min-h-[420px]"
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => drop(col.id)}
          >
            <div className="px-3 py-3 border-b border-white/[0.08] bg-white/[0.02]">
              <div className="flex items-center gap-2">
                {col.id ? (
                  <div className="w-6 h-6 rounded-full bg-amber-500/25 flex items-center justify-center text-[10px] font-bold text-amber-300">{initialsOf(col.label)}</div>
                ) : (
                  <div className="w-6 h-6 rounded-full bg-white/[0.06] flex items-center justify-center text-[10px] font-bold text-white/40">—</div>
                )}
                <div className="text-[12px] font-semibold text-white truncate">{col.label}</div>
                <span className="ml-auto text-[10px] text-white/45 font-mono">{(projectsByPM[col.id || "_un"] || []).length}</span>
              </div>
            </div>
            <div className="p-2 space-y-2">
              {(projectsByPM[col.id || "_un"] || []).map(p => {
                const hb = healthBadge(p.health);
                return (
                  <div
                    key={p.id}
                    draggable
                    onDragStart={() => setDragging(p)}
                    onDragEnd={() => setDragging(null)}
                    className="p-2.5 bg-white/[0.03] border border-white/[0.08] rounded cursor-move hover:border-white/20"
                    data-testid={`pcc-am-card-${p.id}`}
                  >
                    <div className="text-[12px] font-medium text-white truncate">{p.name}</div>
                    <div className="text-[10px] text-white/45 font-mono truncate mt-0.5">{p.client}</div>
                    <div className="flex items-center justify-between mt-2 gap-1">
                      <Badge className={`text-[9px] border ${hb.cls}`}>{hb.label}</Badge>
                      <span className="text-[10px] text-white/60 font-mono">{fmtMoneyShort(p.value)}</span>
                    </div>
                  </div>
                );
              })}
              {(projectsByPM[col.id || "_un"] || []).length === 0 && (
                <div className="text-center py-6 text-white/30 text-[11px]">drop here</div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
